import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { QueryDelayedPayload } from '../../api/schema';
import { CopyToClipboardTooltip } from '../../component/copyToClipboardTooltip/CopyToClipboardTooltip';
import RangeFilter from '../../component/filters/RangeFilter';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getDelayedPayloads, getOperationsDelayedPayloads, operationsDelayedPayloadsFetching } from '../../redux/slices/operations/operationsSlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';

export const DelayedPayloads = (): ReactElement => {
  const dispatch = useDispatch();
  const fetching = useSelector(operationsDelayedPayloadsFetching);
  const delayedPayloads = useSelector(getOperationsDelayedPayloads);
  const [params, setURLParams] = useSearchParams();
  const [pagination, paginationToGet, paginationControl] = useTablePagination({ paginationDirty: delayedPayloads.pagination });

  useEffect(() => {
    dispatch(
      getDelayedPayloads({
        dateCutoff: DateTime.now()
          .minus({ minutes: Number(params.range || '1440') })
          .toUTC()
          .toFormat('yyyy-MM-dd HH:mm'),
        ...paginationToGet,
      }),
    );
  }, [dispatch, params.range, paginationToGet]);

  const columns = useMemo(
    () => [
      {
        Header: 'Organization Name',
        accessor: 'organization_name',
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) => (
          <Link to={`/admin/organizations/organizations/${row.original.organization_id}/details`}>{row.original.organization_name}</Link>
        ),
      },
      {
        Header: 'Device Name',
        accessor: 'device_name',
      },
      {
        Header: 'Device Id',
        accessor: 'device_id',
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) => (
          <CopyToClipboardTooltip
            text={row.original.device_id?.split('-')[0]}
            message={`Copied ${row.original.device_id}`}
            copyText={row.original.device_id}
            tooltipText={row.original.device_id}
          />
        ),
      },
      {
        Header: 'Payload Id',
        accessor: 'payload_id',
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) => (
          <CopyToClipboardTooltip
            text={row.original.payload_id?.split('-')[0]}
            message={`Copied ${row.original.payload_id}`}
            copyText={row.original.payload_id}
            tooltipText={row.original.payload_id}
            ellipsis
          />
        ),
      },

      {
        Header: 'Correlation Id',
        accessor: 'correlation_id',
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) => (
          <CopyToClipboardTooltip
            text={row.original.correlation_id?.split('-')[0]}
            message={`Copied ${row.original.correlation_id}`}
            copyText={row.original.correlation_id}
            tooltipText={row.original.correlation_id}
            ellipsis
          />
        ),
      },
      {
        Header: 'Processed At',
        accessor: 'processed_at',
        Filter: RangeFilter,
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) =>
          row.original.processed_at ? DateTime.fromISO(row.original.processed_at).toFormat('LLL dd, yyyy, hh:mm:ss a') : '',
      },
      {
        Header: 'Prev Processed A',
        accessor: 'prev_processed_at',
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) =>
          row.original.prev_processed_at ? DateTime.fromISO(row.original.prev_processed_at).toFormat('LLL dd, yyyy, hh:mm:ss a') : '',
      },
      {
        Header: 'Minute Delay',
        accessor: 'minute_delay',
        Cell: ({ row }: { row: { original: QueryDelayedPayload } }) => row.original.minute_delay?.toFixed(2).toLocaleString(),
      },
    ],
    [],
  );

  const handleFiltersClear = useCallback(() => {
    setURLParams({ clearAll: true });
  }, [setURLParams]);

  return (
    <>
      {fetching && <LinearProgress />}
      <Card>
        <Table
          columns={columns}
          data={delayedPayloads.result || []}
          loading={fetching}
          showDefaultFilters
          onFiltersClear={handleFiltersClear}
          paginationControl={paginationControl}
          pagination={pagination}
        />
      </Card>
    </>
  );
};
